import React, { useEffect, useRef, useCallback } from'react';
import { gsap } from'../../animations/gsap.config';
import { useIsMobile } from'../../hooks/useMediaQuery';

const CustomCursor = () => {
 const isMobile = useIsMobile();
 const dotRef = useRef(null);
 const outlineRef = useRef(null);
 const labelRef = useRef(null);

 // Trailing position for outline
 const pos = useRef({ x: 0, y: 0 });
 const vel = useRef({ x: 0, y: 0 });

 const handleMouseMove = useCallback((e) => {
 const { clientX: x, clientY: y } = e;

 // Dot follows exactly
 gsap.to(dotRef.current, {
 x, y,
 duration: 0.1,
 ease:'none',
 });

 // Outline lags behind (smooth follow)
 gsap.to(outlineRef.current, {
 x, y,
 duration: 0.55,
 ease:'power3.out',
 });
 }, []);

 const handleMouseEnter = useCallback((e) => {
 const target = e.currentTarget;
 const isLink = target.tagName ==='A' || target.tagName ==='BUTTON';
 const isCard = target.dataset.cursor ==='card';
 const isView = target.dataset.cursor ==='view';
 const label = target.dataset.cursorLabel ||'';

 if (isView || label) {
 // Large circle with text
 gsap.to(outlineRef.current, {
 scale: 3.5,
 backgroundColor:'rgba(29,78,216,0.15)',
 borderColor:'rgba(29,78,216,0.6)',
 duration: 0.4,
 ease:'back.out(1.7)',
 });
 gsap.to(dotRef.current, { scale: 0, duration: 0.3 });
 if (labelRef.current && label) {
 labelRef.current.textContent = label;
 gsap.to(labelRef.current, { opacity: 1, scale: 1, duration: 0.3 });
 }
 } else if (isCard) {
 gsap.to(outlineRef.current, {
 scale: 2.5,
 backgroundColor:'rgba(245,158,11,0.1)',
 borderColor:'rgba(245,158,11,0.5)',
 duration: 0.4,
 ease:'back.out(1.7)',
 });
 gsap.to(dotRef.current, {
 scale: 1.5,
 backgroundColor:'#f59e0b',
 duration: 0.3,
 });
 } else if (isLink) {
 gsap.to(outlineRef.current, {
 scale: 2,
 backgroundColor:'rgba(29,78,216,0.1)',
 borderColor:'rgba(29,78,216,0.8)',
 duration: 0.4,
 ease:'back.out(1.7)',
 });
 gsap.to(dotRef.current, {
 scale: 1.5,
 backgroundColor:'#1d4ed8',
 duration: 0.3,
 });
 }
 }, []);

 const handleMouseLeave = useCallback(() => {
 gsap.to(outlineRef.current, {
 scale: 1,
 backgroundColor:'transparent',
 borderColor:'rgba(29,78,216,0.5)',
 duration: 0.4,
 ease:'back.out(1.7)',
 });
 gsap.to(dotRef.current, {
 scale: 1,
 backgroundColor:'#f59e0b',
 duration: 0.3,
 });
 if (labelRef.current) {
 gsap.to(labelRef.current, {
 opacity: 0,
 scale: 0.8,
 duration: 0.2,
 onComplete: () => {
 if (labelRef.current) labelRef.current.textContent ='';
 },
 });
 }
 }, []);

 const handleMouseDown = useCallback(() => {
 gsap.to([dotRef.current, outlineRef.current], {
 scale: 0.85,
 duration: 0.15,
 ease:'power2.out',
 });
 }, []);

 const handleMouseUp = useCallback(() => {
 gsap.to([dotRef.current, outlineRef.current], {
 scale: 1,
 duration: 0.3,
 ease:'back.out(2)',
 });
 }, []);

 useEffect(() => {
 if (isMobile) return;

 // Hide native cursor
 document.documentElement.style.cursor ='none';

 window.addEventListener('mousemove', handleMouseMove, { passive: true });
 window.addEventListener('mousedown', handleMouseDown);
 window.addEventListener('mouseup', handleMouseUp);

 // Delegate hover events to interactive elements
 const interactiveSelector =
'a, button, [data-cursor], input, textarea, select, label[for]';

 const attachListeners = () => {
 document.querySelectorAll(interactiveSelector).forEach((el) => {
 el.addEventListener('mouseenter', handleMouseEnter);
 el.addEventListener('mouseleave', handleMouseLeave);
 });
 };

 attachListeners();

 // Re-attach on DOM mutations (for dynamically added elements)
 const observer = new MutationObserver(attachListeners);
 observer.observe(document.body, { childList: true, subtree: true });

 return () => {
 document.documentElement.style.cursor ='';
 window.removeEventListener('mousemove', handleMouseMove);
 window.removeEventListener('mousedown', handleMouseDown);
 window.removeEventListener('mouseup', handleMouseUp);

 document.querySelectorAll(interactiveSelector).forEach((el) => {
 el.removeEventListener('mouseenter', handleMouseEnter);
 el.removeEventListener('mouseleave', handleMouseLeave);
 });

 observer.disconnect();
 };
 }, [
 isMobile,
 handleMouseMove,
 handleMouseEnter,
 handleMouseLeave,
 handleMouseDown,
 handleMouseUp,
 ]);

 if (isMobile) return null;

 return (
 <>
 {/* Dot cursor */}
 <div
 ref={dotRef}
 className="fixed top-0 left-0 z-[9999] pointer-events-none"
 style={{
 width:'10px',
 height:'10px',
 borderRadius:'50%',
 backgroundColor:'#f59e0b',
 transform:'translate(-50%, -50%)',
 willChange:'transform',
 mixBlendMode:'difference',
 }}
 />

 {/* Outline cursor */}
 <div
 ref={outlineRef}
 className="fixed top-0 left-0 z-[9998] pointer-events-none flex items-center justify-center"
 style={{
 width:'36px',
 height:'36px',
 borderRadius:'50%',
 border:'1.5px solid rgba(29,78,216,0.5)',
 transform:'translate(-50%, -50%)',
 willChange:'transform',
 transformOrigin:'center',
 }}
 >
 {/* Cursor label */}
 <span
 ref={labelRef}
 className="text-[9px] font-semibold tracking-wider text-white uppercase opacity-0 select-none whitespace-nowrap"
 style={{ transform:'scale(0.8)' }}
 />
 </div>
 </>
 );
};

export default CustomCursor;